import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Users, Download, FolderOpen, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { nl, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_documents: 0, total_users: 0, total_downloads: 0, categories: 0 });
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, recentRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/documents/recent/list`)
      ]);
      setStats(statsRes.data);
      setRecentDocs(recentRes.data);
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type) => {
    const icons = {
      pdf: 'file-pdf',
      doc: 'file-doc',
      docx: 'file-doc',
      xls: 'file-xls',
      xlsx: 'file-xls',
      png: 'file-img',
      jpg: 'file-img',
      jpeg: 'file-img',
    };
    return icons[type] || 'file';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const statCards = [
    { label: t('stats.documents'), value: stats.total_documents, icon: FileText, color: 'text-blue-500' },
    { label: t('stats.users'), value: stats.total_users, icon: Users, color: 'text-green-500' },
    { label: t('stats.downloads'), value: stats.total_downloads, icon: Download, color: 'text-orange-500' },
    { label: t('settings.categories'), value: stats.categories, icon: FolderOpen, color: 'text-purple-500' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          {language === 'nl' ? 'Welkom terug,' : language === 'de' ? 'Willkommen zurück,' : 'Welcome back,'} {user?.name}
        </h1>
        <p className="text-muted-foreground">
          {language === 'nl' 
            ? 'Hier is een overzicht van het documentenportaal'
            : language === 'de'
            ? 'Hier ist eine Übersicht des Dokumentenportals'
            : 'Here is an overview of the document portal'}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-card border-border hover:border-primary/30 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-muted ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent documents */}
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              {t('docs.recent')}
            </CardTitle>
            <Link to="/documents">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                {t('docs.all')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {recentDocs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {language === 'nl' 
                ? 'Nog geen documenten geopend'
                : language === 'de'
                ? 'Noch keine Dokumente geöffnet'
                : 'No documents opened yet'}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentDocs.map((doc) => (
                <Link 
                  key={doc.id} 
                  to={`/documents/${doc.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors"
                  data-testid={`recent-doc-${doc.id}`}
                >
                  <div className={`p-2 rounded-lg bg-muted file-${getFileIcon(doc.file_type)}`}>
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground font-medium truncate">{doc.name}</p>
                    <p className="text-muted-foreground text-sm">
                      {formatFileSize(doc.file_size)} • {doc.file_type.toUpperCase()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground text-sm">
                      {formatDistanceToNow(new Date(doc.created_at), { 
                        addSuffix: true,
                        locale: language === 'nl' ? nl : language === 'de' ? de : enUS 
                      })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
